import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import type { BotPreferences } from '@brandschutz/shared';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/cn';
import { getBotPreferences } from './api';
import { ChatPanel } from './ChatPanel';

/**
 * Floatender Companion-Bot rechts unten.
 *
 * - Position via UserPreference (4 Optionen).
 * - Auf < 640 px komplett ausgeblendet (zu wenig Platz).
 * - Klick öffnet/schließt das ChatPanel.
 * - Subtle Pulse-Animation, gedimmt via motionScale (0 = aus).
 */

const POSITION_CLASSES: Record<BotPreferences['botPosition'], string> = {
  'bottom-right': 'bottom-5 right-5',
  'bottom-left': 'bottom-5 left-5',
  'top-right': 'top-20 right-5',
  'top-left': 'top-20 left-5',
};

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState<boolean>(() =>
    typeof window === 'undefined' ? false : window.innerWidth < 640,
  );
  useEffect(() => {
    const onResize = (): void => setMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

export function BotBubble() {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  // Preferences nur einmal laden — der Endpoint legt sie bei Bedarf an.
  // 401-Fall (kein Login) wird vom api-Wrapper geschluckt → query failt
  // still, und wir blenden den Bot aus.
  const { data: prefs, isError } = useQuery({
    queryKey: ['bot-preferences'],
    queryFn: getBotPreferences,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (isError || isMobile) return null;

  const position = prefs?.botPosition ?? 'bottom-right';
  const visible = prefs?.botVisible ?? true;
  const motionScale = prefs?.motionScale ?? 1.0;

  if (!visible) return null;

  return (
    <>
      <div className={cn('fixed z-40 print:hidden', POSITION_CLASSES[position])}>
        <motion.button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Ignis-AI-Assistant schließen' : 'Ignis-AI-Assistant öffnen'}
          className={cn(
            'group flex h-12 w-12 items-center justify-center rounded-full',
            'border border-ink-900 bg-ink-900 text-paper shadow-elev-3',
            'transition-colors hover:bg-ink-800',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2',
          )}
          animate={
            motionScale > 0
              ? {
                  scale: [1, 1 + 0.03 * motionScale, 1],
                }
              : undefined
          }
          transition={
            motionScale > 0
              ? {
                  duration: 2.4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }
              : undefined
          }
        >
          <Icon
            name={open ? 'X' : 'Sparkles'}
            size={20}
            className="text-accent-400 group-hover:text-accent-300"
          />
        </motion.button>
      </div>

      <AnimatePresence>
        {open ? <ChatPanel position={position} onClose={() => setOpen(false)} /> : null}
      </AnimatePresence>
    </>
  );
}
